import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { EventBooking, CreateBookingData } from '@/types/events';

export function useEventBookings(eventId: string | undefined) {
  return useQuery({
    queryKey: ['event-bookings', eventId],
    queryFn: async (): Promise<EventBooking[]> => {
      if (!eventId) return [];
      
      const { data, error } = await supabase
        .from('event_bookings')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data || []) as EventBooking[];
    },
    enabled: !!eventId,
  });
}

export function useMyBookings() {
  return useQuery({
    queryKey: ['my-bookings'],
    queryFn: async (): Promise<EventBooking[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('event_bookings')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data || []) as EventBooking[];
    },
  });
}

interface CreateBookingResponse {
  success: boolean;
  booking_id: string;
  confirmation_token: string;
  booking_code?: string;
  message: string;
}

interface CreateBookingError {
  error: string;
  code: string;
  available?: number;
}

export function useCreateBooking() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateBookingData): Promise<EventBooking> => {
      // Call edge function for public booking
      const { data: response, error } = await supabase.functions.invoke<CreateBookingResponse | CreateBookingError>(
        'create-event-booking',
        {
          body: {
            event_id: data.event_id,
            occurrence_id: data.occurrence_id || null,
            name: data.guest_name || '',
            email: data.guest_email,
            quantity: data.spots,
          },
        }
      );

      if (error) {
        console.error('Edge function error:', error);
        throw new Error('Kunde inte skapa bokningen. Försök igen.');
      }

      // Check for error response from edge function
      if (response && 'error' in response) {
        throw new Error(response.error);
      }

      if (!response || !('booking_id' in response)) {
        throw new Error('Oväntat svar från servern');
      }

      // Return a minimal EventBooking object with data we have
      // The actual booking was created on the server
      return {
        id: response.booking_id,
        event_id: data.event_id,
        producer_id: data.producer_id,
        occurrence_id: data.occurrence_id || null,
        user_id: null,
        guest_name: data.guest_name || null,
        guest_email: data.guest_email,
        spots: data.spots,
        status: 'confirmed',
        confirmation_token: response.confirmation_token,
        booking_code: response.booking_code || '',
        confirmation_sent_at: null,
        cancelled_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as EventBooking;
    },
    onSuccess: (booking) => {
      queryClient.invalidateQueries({ queryKey: ['event-bookings', booking.event_id] });
      queryClient.invalidateQueries({ queryKey: ['public-event', booking.event_id] });
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      // Also invalidate occurrence bookings if present
      if (booking.occurrence_id) {
        queryClient.invalidateQueries({ queryKey: ['event-occurrences', booking.event_id] });
        queryClient.invalidateQueries({ queryKey: ['public-event-occurrences', booking.event_id] });
      }
    },
  });
}

export function useUpdateBookingStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updateData: { status: string; cancelled_at?: string } = { status };
      
      // Set cancelled_at timestamp when cancelling
      if (status === 'cancelled') {
        updateData.cancelled_at = new Date().toISOString();
      }
      
      const { data: booking, error } = await supabase
        .from('event_bookings')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return booking as EventBooking;
    },
    onSuccess: (booking) => {
      queryClient.invalidateQueries({ queryKey: ['event-bookings', booking.event_id] });
      queryClient.invalidateQueries({ queryKey: ['public-event', booking.event_id] });
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
    },
  });
}
